import React from 'react';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ControlContainer from './forms/control_container';

/**
 * @visibleName Input Field
 */

const InputField = React.forwardRef((props, ref) => {
  const {
    'data-aid': dataAid,
    action,
    autoFocus,
    className,
    description,
    disabled,
    error,
    inputType = 'text',
    label,
    maxLength,
    onBlur,
    onChange,
    success,
    value,
    ...otherProperties
  } = props;
  const onInputChange = ev => {
    let val = isNil(ev.target.value) ? '' : ev.target.value;
    let isValid = true;
    let fldValue = val || '';

    if (inputType === 'number') {
      [fldValue, isValid] = getNumberValue(val);
      if (!isValid) {
        return false;
      }
    }
    if (onChange) {
      onChange(fldValue);
    }
  };

  return (
    <FieldContained
      className={className}
      control={
        <FieldWrapper disabled={disabled} isError={!!error}>
          <StyledInput
            autoFocus={autoFocus}
            ref={ref}
            {...otherProperties}
            data-aid={dataAid}
            disabled={disabled}
            maxLength={maxLength ? maxLength : inputType === 'number' ? 20 : maxLength}
            onBlur={onBlur}
            onChange={onInputChange}
            type={inputType === 'password' ? 'password' : 'text'}
            value={value || value === 0 ? value : ''}
          />
          {action}
        </FieldWrapper>
      }
      data-aid={dataAid}
      description={description}
      error={error}
      label={label}
      success={success}
    />
  );
});

export const getNumberValue = value => {
  if (!value) {
    return ['', true];
  }
  if (typeof value === 'number') {
    return [value, true];
  }
  let val = value.trim();
  if ((val.length === 1 && ['+', '-'].includes(val)) || val.endsWith('.')) {
    val += '0';
  }
  if (/^[+-]?\d+(\.\d+)?$/.test(val)) {
    return [parseFloat(val), true];
  }

  return ['', false];
};

InputField.propTypes = {
  'data-aid': PropTypes.string,
  action: PropTypes.element,
  autoFocus: PropTypes.bool,
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  error: PropTypes.string,
  inputType: PropTypes.oneOf(['number', 'text', 'password']),
  label: PropTypes.string,
  maxLength: PropTypes.number,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  success: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export const FieldContained = styled(ControlContainer)`
  font-size: ${p => p.theme.fontSize.base};
`;

export const FieldWrapper = styled.div`
  display: flex;
  background-color: ${p => p.theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${p => (p.isError ? p.theme.colors.red400 : p.theme.colors.gray300)};
  font-size: ${p => p.theme.fontSize.base};
  height: 32px;
  padding: 7px 8px; // changed from '8px 8px' to compensate bottom clipping of characters 'g'/'j'/'p'
  transition: border-color 100ms ease-in;
  width: 100%;

  &:focus-within {
    border-color: ${p => (p.isError ? p.theme.colors.red600 : p.theme.colors.gray600)};
  }

  &[disabled] {
    background-color: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray500};
  }
`;

export const InputActionButton = styled.button`
  display: inline-flex;
  margin-left: 6px;
  cursor: pointer;
  border: none;
  outline: none;
  background-color: transparent;
  padding: 0;
`;

export const StyledInput = styled.input`
  border: none;
  flex: 1 1 auto;
  font-size: inherit;
  line-height: 1.16;

  &:focus {
    outline: none;
  }
  ::placeholder {
    color: ${p => p.theme.colors.gray500};
  }
  &[disabled] {
    background-color: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray500};
  }
`;

export default InputField;
